/**
 * CustomerSelector component for SnapHub PWA.
 *
 * Replaces the free-text customer number input in the cart page.
 * Shows a searchable dropdown of the user's maintained customers.
 * Falls back to manual input if no customers are maintained.
 *
 * Features:
 * - Dropdown with search/filter
 * - Shows customer name + number
 * - Keyboard accessible
 * - Falls back to text input when list is empty
 * - Link to customer management page
 */

import { customerService } from '../../features/customers/customer.service';
import { navigate } from '../../infrastructure/router';
import type { CustomerEntry } from '../models/customer-entry.model';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface CustomerSelectorOptions {
  /** Currently selected customer ID */
  value: string;
  /** Callback when a customer is selected */
  onChange: (customerId: string, customerName: string) => void;
}

export interface CustomerSelectorHandle {
  /** Get the current value */
  getValue: () => string;
  /** Set the value programmatically */
  setValue: (customerId: string) => void;
  /** Destroy the component */
  destroy: () => void;
}

// ---------------------------------------------------------------------------
// SVG Icons
// ---------------------------------------------------------------------------

const chevronIcon = `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"/></svg>`;

const manageIcon = `<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06A1.65 1.65 0 0 0 9 4.68 1.65 1.65 0 0 0 10 3.17V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>`;

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Render a customer selector into the given container.
 * Returns a handle for programmatic control.
 */
export function renderCustomerSelector(
  container: HTMLElement,
  options: CustomerSelectorOptions,
): CustomerSelectorHandle {
  let currentValue = options.value;
  let isOpen = false;
  let filterText = '';
  let highlightIndex = -1;

  // Wrapper element
  const wrapper = document.createElement('div');
  wrapper.className = 'customer-selector';

  // Build the component
  renderSelector();
  container.appendChild(wrapper);

  // Re-render when customer list changes (e.g. async load completes)
  const unsubscribeCustomers = customerService.onCustomersChanged(() => {
    // Only re-render if not currently open (to avoid disrupting user interaction)
    if (!isOpen) {
      renderSelector();
    }
  });

  // Close dropdown on outside mousedown (mousedown fires before click,
  // avoiding the issue where the trigger's click re-opens after close)
  const handleOutsideClick = (e: MouseEvent) => {
    if (!wrapper.contains(e.target as Node)) {
      closeDropdown();
    }
  };
  document.addEventListener('mousedown', handleOutsideClick);

  function renderSelector(): void {
    wrapper.innerHTML = '';

    const customers = customerService.getCustomers();

    if (customers.length === 0) {
      // Fallback: show text input + link to manage customers
      renderFallbackInput();
      return;
    }

    // Selected display / trigger button
    const trigger = document.createElement('button');
    trigger.type = 'button';
    trigger.className = 'customer-selector__trigger';
    trigger.setAttribute('aria-haspopup', 'listbox');
    trigger.setAttribute('aria-expanded', String(isOpen));

    const selectedCustomer = customers.find((c) => c.customer_id === currentValue);
    if (selectedCustomer) {
      trigger.innerHTML = `
        <span class="customer-selector__selected">
          <span class="customer-selector__selected-name">${escapeHtml(selectedCustomer.name)}</span>
          <span class="customer-selector__selected-id">${escapeHtml(selectedCustomer.customer_id)}</span>
        </span>
        <span class="customer-selector__chevron">${chevronIcon}</span>
      `;
    } else if (currentValue) {
      trigger.innerHTML = `
        <span class="customer-selector__selected">
          <span class="customer-selector__selected-name">${escapeHtml(currentValue)}</span>
        </span>
        <span class="customer-selector__chevron">${chevronIcon}</span>
      `;
    } else {
      trigger.innerHTML = `
        <span class="customer-selector__placeholder">Kunde auswählen...</span>
        <span class="customer-selector__chevron">${chevronIcon}</span>
      `;
    }

    trigger.addEventListener('mousedown', (e) => {
      // Prevent the outside-click handler from firing for this click
      e.stopPropagation();
    });

    trigger.addEventListener('click', (e) => {
      e.stopPropagation();
      if (isOpen) {
        closeDropdown();
      } else {
        openDropdown();
      }
    });

    wrapper.appendChild(trigger);

    // Dropdown panel
    if (isOpen) {
      const dropdown = document.createElement('div');
      dropdown.className = 'customer-selector__dropdown';

      // Prevent mousedown inside dropdown from closing it
      dropdown.addEventListener('mousedown', (e) => {
        e.stopPropagation();
      });

      // Search input
      const searchInput = document.createElement('input');
      searchInput.type = 'search';
      searchInput.className = 'customer-selector__search';
      searchInput.placeholder = 'Suchen...';
      searchInput.value = filterText;
      searchInput.setAttribute('aria-label', 'Kunden filtern');
      searchInput.addEventListener('input', () => {
        filterText = searchInput.value;
        highlightIndex = -1;
        renderDropdownList(dropdown, customers);
      });
      searchInput.addEventListener('keydown', (e) => {
        handleKeydown(e, dropdown, customers);
      });
      dropdown.appendChild(searchInput);

      // List
      renderDropdownList(dropdown, customers);

      // Footer: manage link
      const footer = document.createElement('div');
      footer.className = 'customer-selector__footer';

      const manageLink = document.createElement('button');
      manageLink.type = 'button';
      manageLink.className = 'customer-selector__manage-link';
      manageLink.innerHTML = `${manageIcon} <span>Kunden verwalten</span>`;
      manageLink.addEventListener('click', () => {
        closeDropdown();
        navigate('/customers');
      });
      footer.appendChild(manageLink);
      dropdown.appendChild(footer);

      wrapper.appendChild(dropdown);

      // Focus search input
      requestAnimationFrame(() => searchInput.focus());
    }
  }

  function renderFallbackInput(): void {
    const inputWrapper = document.createElement('div');
    inputWrapper.className = 'customer-selector__fallback';

    const input = document.createElement('input');
    input.type = 'text';
    input.className = 'customer-selector__fallback-input';
    input.placeholder = 'Kundennummer eingeben...';
    input.value = currentValue;
    input.setAttribute('aria-label', 'Kundennummer');
    input.addEventListener('input', () => {
      currentValue = input.value;
      options.onChange(input.value, '');
    });

    const manageLink = document.createElement('button');
    manageLink.type = 'button';
    manageLink.className = 'customer-selector__manage-link customer-selector__manage-link--inline';
    manageLink.innerHTML = `${manageIcon} <span>Kunden pflegen</span>`;
    manageLink.addEventListener('click', () => navigate('/customers'));

    inputWrapper.appendChild(input);
    inputWrapper.appendChild(manageLink);
    wrapper.appendChild(inputWrapper);
  }

  function renderDropdownList(dropdown: HTMLElement, customers: CustomerEntry[]): void {
    // Remove existing list
    const existingList = dropdown.querySelector('.customer-selector__list');
    if (existingList) existingList.remove();

    const filtered = filterCustomerList(customers, filterText);

    const list = document.createElement('ul');
    list.className = 'customer-selector__list';
    list.setAttribute('role', 'listbox');

    if (filtered.length === 0) {
      const empty = document.createElement('li');
      empty.className = 'customer-selector__empty';
      empty.textContent = 'Keine Treffer';
      list.appendChild(empty);
    } else {
      filtered.forEach((customer, index) => {
        const item = document.createElement('li');
        item.className = 'customer-selector__item';
        item.setAttribute('role', 'option');
        item.setAttribute('aria-selected', String(customer.customer_id === currentValue));

        if (customer.customer_id === currentValue) {
          item.classList.add('customer-selector__item--selected');
        }
        if (index === highlightIndex) {
          item.classList.add('customer-selector__item--highlighted');
        }

        item.innerHTML = `
          <span class="customer-selector__item-name">${escapeHtml(customer.name)}</span>
          <span class="customer-selector__item-id">${escapeHtml(customer.customer_id)}</span>
        `;

        item.addEventListener('click', () => {
          selectCustomer(customer);
        });

        list.appendChild(item);
      });
    }

    // Insert after search input
    const searchInput = dropdown.querySelector('.customer-selector__search');
    if (searchInput && searchInput.nextSibling) {
      dropdown.insertBefore(list, searchInput.nextSibling);
    } else {
      const footer = dropdown.querySelector('.customer-selector__footer');
      if (footer) {
        dropdown.insertBefore(list, footer);
      } else {
        dropdown.appendChild(list);
      }
    }
  }

  function handleKeydown(e: KeyboardEvent, dropdown: HTMLElement, customers: CustomerEntry[]): void {
    const filtered = filterCustomerList(customers, filterText);

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        highlightIndex = Math.min(highlightIndex + 1, filtered.length - 1);
        renderDropdownList(dropdown, customers);
        break;
      case 'ArrowUp':
        e.preventDefault();
        highlightIndex = Math.max(highlightIndex - 1, 0);
        renderDropdownList(dropdown, customers);
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightIndex >= 0 && highlightIndex < filtered.length) {
          selectCustomer(filtered[highlightIndex]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        closeDropdown();
        break;
    }
  }

  function selectCustomer(customer: CustomerEntry): void {
    currentValue = customer.customer_id;
    options.onChange(customer.customer_id, customer.name);
    closeDropdown();
  }

  function openDropdown(): void {
    isOpen = true;
    filterText = '';
    highlightIndex = -1;
    renderSelector();
  }

  function closeDropdown(): void {
    if (!isOpen) return;
    isOpen = false;
    filterText = '';
    highlightIndex = -1;
    renderSelector();
  }

  function filterCustomerList(customers: CustomerEntry[], query: string): CustomerEntry[] {
    if (!query.trim()) return customers;
    const lower = query.toLowerCase().trim();
    return customers.filter(
      (c) =>
        c.customer_id.toLowerCase().includes(lower) ||
        c.name.toLowerCase().includes(lower),
    );
  }

  // Handle
  const handle: CustomerSelectorHandle = {
    getValue: () => currentValue,
    setValue: (customerId: string) => {
      currentValue = customerId;
      renderSelector();
    },
    destroy: () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      unsubscribeCustomers();
      wrapper.remove();
    },
  };

  return handle;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function escapeHtml(str: string): string {
  const div = document.createElement('div');
  div.textContent = str;
  return div.innerHTML;
}
